import { useSyncExternalStore } from "react";
import { logError } from "@/lib/log";

const UPDATE_CHECK_MS = 60 * 60 * 1000;

let waiting: ServiceWorker | null = null;
const listeners = new Set<() => void>();

function setWaiting(worker: ServiceWorker) {
  waiting = worker;
  listeners.forEach((l) => l());
}

// A new version installs in the background and waits; it takes over only when the employee taps
// "update" or reopens the app, so a running page never mixes old code with a new cache.
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;

  let controlled = Boolean(sw.controller);
  sw.addEventListener("controllerchange", () => {
    if (controlled) window.location.reload();
    controlled = true;
  });

  window.addEventListener("load", () => {
    sw.register("/sw.js", { updateViaCache: "none" }).then(
      (registration) => {
        const track = (worker: ServiceWorker | null) => {
          if (!worker) return;
          const check = () => {
            if (worker.state === "installed" && sw.controller) setWaiting(worker);
          };
          check();
          worker.addEventListener("statechange", check);
        };
        track(registration.waiting);
        registration.addEventListener("updatefound", () => track(registration.installing));

        const update = () => void registration.update().catch(() => undefined);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") update();
        });
        setInterval(update, UPDATE_CHECK_MS);
      },
      (e: unknown) => logError("sw_register_failed", e),
    );
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUpdateReady(): boolean {
  return useSyncExternalStore(subscribe, () => waiting !== null);
}

export function applyUpdate(): void {
  waiting?.postMessage("skip-waiting");
}
